import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Phone } from "lucide-react";
import { payersApi } from "../api";
import { Button } from "./ui/button";

const STATUS_BADGES = {
  "VERIFIED IN NETWORK": { label: "Participating", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  "AUTHORIZED PROGRAM": { label: "Program Provider", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  "CONTRACTING IN PROGRESS": { label: "Participation being finalized", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  "APPLICATION SUBMITTED": { label: "Application submitted — call to verify", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  "VERIFY ELIGIBILITY": { label: "Call to verify", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const PayerTile = ({ payer }) => {
  const [imgOk, setImgOk] = useState(true);
  const logoUrl = payer.logoUrl || "";
  const badge = payer.badgeText
    ? { label: payer.badgeText, cls: STATUS_BADGES[payer.status]?.cls || "bg-slate-100 text-slate-600 border-slate-200" }
    : STATUS_BADGES[payer.status];

  return (
    <div
      data-testid={`payer-tile-${payer.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="snap-center shrink-0 w-[220px] md:w-[230px] bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col items-center text-center"
    >
      <div className="h-12 flex items-center justify-center mb-3">
        {logoUrl && imgOk ? (
          <img
            src={logoUrl}
            alt={`${payer.programName || payer.name} logo`}
            className="h-10 max-w-[160px] object-contain"
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div
            className="font-display font-extrabold text-xl px-2"
            style={{ color: payer.color }}
            aria-label={`${payer.programName || payer.name} logo`}
          >
            {payer.name}
          </div>
        )}
      </div>
      <div className="font-semibold text-sm text-[#0E2A33] leading-snug">{payer.name}</div>
      {payer.programName && payer.programName !== payer.name && (
        <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">{payer.programName}</div>
      )}
      {badge && (
        <span className={`mt-2 inline-block text-[11px] font-medium px-2.5 py-1 rounded-full border ${badge.cls}`}>
          {badge.label}
        </span>
      )}
      {payer.description && (
        <p className="text-[11px] text-slate-500 mt-2 leading-snug">{payer.description}</p>
      )}
    </div>
  );
};

const Row = ({ title, payers }) => {
  if (!payers.length) return null;
  return (
    <div className="mt-8">
      <div className="text-center text-xs font-semibold uppercase tracking-widest text-slate-400 mb-4">{title}</div>
      <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 px-1 md:flex-wrap md:justify-center md:overflow-visible">
        {payers.map((p) => <PayerTile key={p.id} payer={p} />)}
      </div>
    </div>
  );
};

const PayerStrip = ({ compact = false, ctaHref = "/mycare" }) => {
  const [payers, setPayers] = useState([]);

  useEffect(() => {
    payersApi.publicList().then((r) => setPayers(r.data)).catch(() => {});
  }, []);

  if (!payers.length) return null;

  const insurance = payers.filter((p) => p.category === "insurance");
  const programs = payers.filter((p) => p.category !== "insurance" && !p.name.toUpperCase().includes("DODD"));
  const dodd = payers.find((p) => p.name.toUpperCase().includes("DODD"));

  const cta = ctaHref.startsWith("#") ? (
    <a href={ctaHref}>
      <Button data-testid="verify-coverage-btn" className="rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white h-12 px-8 font-semibold tracking-wide">
        VERIFY MY COVERAGE
      </Button>
    </a>
  ) : (
    <Link to={ctaHref}>
      <Button data-testid="verify-coverage-btn" className="rounded-full bg-[#0E5A72] hover:bg-[#0A4557] text-white h-12 px-8 font-semibold tracking-wide">
        VERIFY MY COVERAGE
      </Button>
    </Link>
  );

  return (
    <section data-testid="payer-strip" className={`${compact ? "py-12" : "section"} bg-[#F7FBFC] border-y border-slate-100`}>
      <div className="mx-auto max-w-7xl px-4">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-display font-bold text-2xl md:text-3xl text-[#0E2A33]">
            Insurance & Program Participation
          </h2>
          <p className="text-slate-500 mt-3 text-sm md:text-base">
            Coverage and service authorization vary by plan. Contact Caregivers Alliance Homecare to
            verify eligibility, authorization, and current network participation before services begin.
          </p>
        </div>

        <Row title="Insurance / Managed Care" payers={insurance} />
        <Row title="Programs & Waivers" payers={[...programs, ...(dodd ? [dodd] : [])]} />

        {dodd && (
          <div data-testid="dodd-certified-line" className="mt-8 flex justify-center">
            <div className="inline-flex items-center gap-3 bg-white rounded-full border border-slate-200 shadow-sm px-6 py-3">
              <Award className="h-5 w-5 text-[#C8102E]" />
              <div className="text-sm">
                <span className="font-semibold text-[#0E2A33]">Ohio DODD Certified Provider</span>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-6 max-w-2xl mx-auto">
          Eligibility, authorization and network participation must be verified before services begin.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
          {cta}
          <a href="tel:6145261548" className="inline-flex items-center gap-2 text-[#0E5A72] font-medium text-sm hover:underline">
            <Phone className="h-4 w-4" /> (614) 526-1548
          </a>
        </div>
      </div>
    </section>
  );
};

export default PayerStrip;
